// todo Probably rename the ProjectsFilter to DataSheetsFilter, which is really what it is
import { useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Box } from '@mui/material';
import type { ResultFilter } from '@/schema.graphql';
import { Button } from '../Button';
import FilterDialog from '../FilterDialog';
import type { SelectOptions } from '../FilterDialog/useSelectWithCheckboxesProps.ts';
import ProjectMetaSelect from './ProjectMetaSelect';
import { ProjectBudgetFilter } from './ProjectBudgetFilter.tsx';
import type { ProjectsFilter_team$key } from './__generated__/ProjectsFilter_team.graphql';
import type {
    ProjectsFilter_sheets$data,
    ProjectsFilter_sheets$key,
} from './__generated__/ProjectsFilter_sheets.graphql';
import type { ProjectsFilter_questionnaire$key } from './__generated__/ProjectsFilter_questionnaire.graphql';
import type { ProjectBudgetFilterValue } from './useProjectBudgetFilterDescription.ts';

const META_QUESTION_ID_FOR_SURVEY_TYPE = '8ae0a0cc-ab1b-4427-8323-dab7b21452fe';

const messages = defineMessages({
    searchTextFieldLabel: {
        defaultMessage: 'Search project',
    },
});

// This function will map the array of string to
// an option object.
function mapValuesToOptions(
    values: ReadonlyArray<string>,
    options: SelectOptions,
): SelectOptions {
    return values
        .filter(Boolean)
        .map((id) => options.find((option) => option.value === id));
}

// Map the items, these data, is likely coming from the server.
function mapItemsToOptions(
    items: ReadonlyArray<{ readonly id: string; readonly name: string }>,
) {
    return items.map((item) => ({
        label: item.name,
        value: item.id,
    }));
}

function mapOptionsToValues(options: SelectOptions) {
    return options.map((option) => option.value);
}

function anySheetHasMetaKey(
    sheets: ProjectsFilter_sheets$data,
    metaKey: string,
) {
    return sheets.some((sheet) =>
        sheet.surveyMeta.some((s) => s.key === metaKey),
    );
}

function anySheetHasMetaKeyValue(
    sheets: ProjectsFilter_sheets$data,
    metaKey: string,
    value: number,
) {
    return sheets.some((sheet) =>
        sheet.surveyMeta.some((s) => s.key === metaKey && value === s.value),
    );
}

export type ProjectsFilterType = {
    readonly metaResults: ReadonlyArray<ResultFilter>;
    readonly projectCategories: SelectOptions;
    readonly projects: SelectOptions;
    readonly sharedProjects: SelectOptions;
    readonly projectBudget: ProjectBudgetFilterValue;
    readonly fromDate: Date | null;
    readonly toDate: Date | null;
    readonly surveyDateField: 'surveyDate' | 'endDate';
};

export type ProjectsFilterProps = {
    onClose: () => void;
    questionnaire: ProjectsFilter_questionnaire$key;
    filter: ProjectsFilterType;
    handleFiltersChange(filter: ProjectsFilterType): void;
    sheets: ProjectsFilter_sheets$key;
    team: ProjectsFilter_team$key;
};

export function ProjectsFilter(newProps: ProjectsFilterProps) {
    const { filter, handleFiltersChange, onClose } = newProps;
    const team = useFragment(fragments.team, newProps.team);
    const sheets = useFragment(fragments.sheets, newProps.sheets);
    const questionnaire = useFragment(
        fragments.questionnaire,
        newProps.questionnaire,
    );
    const [projectFilters, setProjectFilters] = useState(filter.metaResults);
    const [projectBudget, setProjectBudget] = useState(filter.projectBudget);
    const [sharedProjects] = useState<SelectOptions>(
        filter.sharedProjects || [],
    );

    const { formatMessage } = useIntl();

    const sharedProjectsOptions: SelectOptions = [
        {
            label: formatMessage({
                defaultMessage: 'Projects shared with others',
            }),
            value: 'sharedWithOthers',
        },
        {
            label: formatMessage({
                defaultMessage: 'Projects shared with my team',
            }),
            value: 'sharedWithMyTeam',
        },
        {
            label: formatMessage({ defaultMessage: "'My team's projects" }),
            value: 'onlyMyTeam',
        },
    ];

    const projectsSelect = FilterDialog.useFilterSelectWithCheckboxesProps(
        formatMessage({ defaultMessage: 'Projects' }),
        mapOptionsToValues(filter.projects),
        mapItemsToOptions(team.portfolio.projects),
    );
    const projectCategoriesSelect = FilterDialog.useSelectWithCheckboxesProps(
        formatMessage({ defaultMessage: 'Project categories' }),
        mapOptionsToValues(filter.projectCategories),
        mapItemsToOptions(team.projectCategories),
    );
    const sharedProjectsSelect = FilterDialog.useSelectWithCheckboxesProps(
        formatMessage({ defaultMessage: 'Shared projects' }),
        sharedProjects.map((project) => project.value),
        sharedProjectsOptions,
    );

    const closeAndSave = () => {
        handleFiltersChange({
            metaResults: projectFilters.filter((f) => f.metaValues.length > 0),
            projects: mapValuesToOptions(
                projectsSelect.values,
                projectsSelect.options,
            ),
            projectCategories: mapValuesToOptions(
                projectCategoriesSelect.values,
                projectCategoriesSelect.options,
            ),
            sharedProjects: mapValuesToOptions(
                sharedProjectsSelect.values,
                sharedProjectsSelect.options,
            ),
            projectBudget,
            surveyDateField: filter.surveyDateField,
            fromDate: filter.fromDate,
            toDate: filter.toDate,
        });
        onClose();
    };

    const onChange = (metaKey: string, hasMetaValues: string[]) => {
        let hasFilter = false;
        const metaValues = hasMetaValues.filter(Boolean);
        const filters = projectFilters.map((filter) => {
            if (filter.metaKey === metaKey) {
                hasFilter = true;
                return {
                    metaKey,
                    metaValues,
                };
            }
            return filter;
        });

        if (!hasFilter) {
            filters.push({ metaKey, metaValues });
        }
        setProjectFilters(filters);
    };

    const renderFilters = (metaResults: ProjectsFilter_sheets$data) => {
        const metaList = questionnaire.questions
            .filter(
                (question) =>
                    question.isSurveyMeta &&
                    question.originalId !== META_QUESTION_ID_FOR_SURVEY_TYPE &&
                    anySheetHasMetaKey(sheets, question.originalId),
            )
            .map((question) => {
                return {
                    label: question.shortForm,
                    options: questionnaire.responseScales
                        .find(
                            ({ originalId }) =>
                                originalId === question.responseScaleId,
                        )
                        ?.responseOptions.filter(({ value }) =>
                            anySheetHasMetaKeyValue(
                                sheets,
                                question.originalId,
                                value,
                            ),
                        )
                        .map(({ label, value }) => ({
                            label: label,
                            value: value.toString(), // graphql wants strings for metaValue
                        })),
                    metaKey: question.originalId,
                    metaValues:
                        projectFilters
                            .filter(
                                ({ metaKey }) =>
                                    metaKey === question.originalId,
                            )
                            .map(({ metaValues }) => metaValues)
                            .pop() || [],
                };
            });

        return (
            <div>
                {metaList.map((meta) => (
                    <ProjectMetaSelect
                        key={meta.metaKey}
                        onChange={onChange}
                        questionnaireId={questionnaire.originalId}
                        //metaResults={metaResults}
                        //loadingMetaResults={false}
                        {...meta}
                    />
                ))}
            </div>
        );
    };

    return (
        <Box p={0.5}>
            {renderFilters(sheets)}
            <ProjectBudgetFilter
                value={projectBudget}
                onChange={setProjectBudget}
            />
            <FilterDialog.FilterSelectWithCheckboxes
                searchTextFieldLabel={formatMessage(
                    messages.searchTextFieldLabel,
                )}
                {...projectsSelect}
            />
            <FilterDialog.SelectWithCheckboxes {...projectCategoriesSelect} />
            <FilterDialog.SelectWithCheckboxes {...sharedProjectsSelect} />
            <Button
                sx={{ mt: 0.5 }}
                type="button"
                fullWidth
                onClick={() => closeAndSave()}
            >
                <FormattedMessage
                    defaultMessage="Apply Filter"
                    description="Filter Respondents Button"
                />
            </Button>
        </Box>
    );
}

const fragments = {
    team: graphql`
        fragment ProjectsFilter_team on Team
        @argumentDefinitions(
            portfolioPath: { type: "String", defaultValue: "" }
            onlyMyProjects: { type: "Boolean", defaultValue: false }
        ) {
            projectCategories {
                id
                name
            }

            portfolio(path: $portfolioPath) {
                projects(onlyMyProjects: $onlyMyProjects) {
                    __typename
                    id
                    name
                    ... on Project{
                        projectTeamShares {
                            id
                        }
                    }
                }
            }
        }
    `,
    sheets: graphql`
        fragment ProjectsFilter_sheets on DataSheet
        @relay(plural: true) {
            surveyMeta {
                key: originalKey # This is a question raw id 
                value # This is a response option value (integer)
            }
        }
    `,
    questionnaire: graphql`
        fragment ProjectsFilter_questionnaire on QuestionnaireInterface {
            originalId
            name
            questions {
                originalId
                type
                shortForm
                responseScaleId: originalResponseScaleId
                isSurveyMeta
            }

            responseScales {
                originalId
                responseOptions {
                    value
                    label
                    displayValue
                }
            }
        }
    `,
};
